import cx from 'classnames'
import { useMeasure, useMountEffect } from '@react-hookz/web'
import { clamp, dropRight, last } from 'lodash'
import React, { CSSProperties, useCallback, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react'
import styles from './TagInput.module.scss'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ArrowRight, Close, Plus } from 'blaze-ui/icons'

const MIN_INPUT_WIDTH = 178
const GAP_BETWEEN_TAGS = 6
const ADD_PILL_WIDTH = 78 // 78px is the width of the '+ Add' pill, needs to be set as a constant because it's used in the wrapper's width calculation and otherwise that would be messed up when it's hidden

export type TagInputProps = {
  tags: string[]
  onChange?: (tags: string[]) => void
  placeholder?: string | React.ReactNode
  splitOn?: string
  maxWidth?: boolean
  'aria-label'?: string
  placeholderClassName?: string
  wrapperClassName?: string
  autoFocus?: boolean
  tagColor?: string
  error?: string
  minHeight?: string | number
  useAddPill?: boolean
  size?: 'sm' | 'md'
  showRemoveButton?: boolean
  className?: string
  variant?: 'default' | 'modern' | 'light'
  inputWithBorder?: boolean
  addMorePlaceholder?: string
  transparent?: boolean
}

type pillEditState = { isEditing: false } | { isEditing: true; isNew: boolean; idx: number; tagValue: string }
type pillEditAction =
  | { type: 'startEdit'; idx: number; caretPosition?: number }
  | { type: 'insert'; afterIdx: number }
  | { type: 'update'; tagValue: string }
  | { type: 'end' }
  | { type: 'abort' }

const DefaultPlaceholder = () => {
  const { isMobile } = useResponsiveDesign()

  return (
    <>
      Use <kbd>tab</kbd> or <kbd>,</kbd> to separate {isMobile ? '' : 'keywords'}
    </>
  )
}

export const TagInput: React.FC<React.PropsWithChildren<TagInputProps>> = ({
  tags,
  onChange,
  maxWidth,
  placeholder = <DefaultPlaceholder />,
  splitOn = ',',
  placeholderClassName,
  wrapperClassName,
  tagColor = '#FFFFFF',
  error,
  minHeight,
  useAddPill = false,
  size = 'sm',
  showRemoveButton = true,
  className,
  variant = 'default',
  inputWithBorder = false,
  addMorePlaceholder,
  transparent = false,
  ...rest
}) => {
  const tagRefs = useMemo(() => tags.map(() => React.createRef<HTMLDivElement>()), [tags])
  const editedTagContentEditableRef = useRef<HTMLSpanElement>(null)
  const [wrapperMeasure, wrapperRef] = useMeasure<HTMLDivElement>()
  const [pillsAtEndOfLine, setPillsAtEndOfLine] = useState(new Map<number, number>())
  const [inputState, setInputState] = useState('')
  const [showAddPill, setShowAddPill] = useState(true)
  const inputRef = useRef<HTMLInputElement>(null)
  const inputSpacerRef = useRef<HTMLSpanElement>(null)
  const [inputWidth, setInputWidth] = useState(0)
  const thickness_wrapper_right_wall = useRef(0)

  // Not using the useIsSystemRedesign hook because it would cause lots of tests that are not wrapped in redux Providers and that render TagInputs to fail
  const isSystemRedesignEnabled = document.documentElement.getAttribute('data-autopilot-system-redesign') === 'on'

  useMountEffect(() => {
    if (!wrapperRef.current) return
    const { borderRightWidth, paddingRight } = window.getComputedStyle(wrapperRef.current)
    thickness_wrapper_right_wall.current = parseInt(borderRightWidth) + parseInt(paddingRight)
  })

  const { red, green, blue } = useMemo(
    () => ({
      red: parseInt(tagColor.slice(1, 3), 16),
      green: parseInt(tagColor.slice(3, 5), 16),
      blue: parseInt(tagColor.slice(5, 7), 16),
    }),
    [tagColor],
  )

  const focusEditedTag = (caretPosition?: number) => {
    setTimeout(() => {
      editedTagContentEditableRef.current?.focus()
      if (caretPosition) {
        const range = document.createRange()
        const selectionTargetNode = editedTagContentEditableRef.current?.firstChild as Text
        range.setStart(selectionTargetNode, caretPosition)
        range.setEnd(selectionTargetNode, caretPosition)
        const selection = window.getSelection()
        selection?.removeAllRanges()
        selection?.addRange(range)
      }
    }, 0)
  }

  const pillEditReducer = useCallback(
    (prevState: pillEditState, action: pillEditAction): pillEditState => {
      switch (action.type) {
        case 'startEdit': {
          focusEditedTag(action.caretPosition)
          return { isEditing: true, isNew: false, idx: action.idx, tagValue: tags[action.idx] }
        }
        case 'insert': {
          focusEditedTag()
          return { isEditing: true, isNew: true, idx: action.afterIdx, tagValue: '' }
        }
        case 'update': {
          if (!prevState.isEditing) break
          const endingWithSplitOnRegExp = new RegExp(`^${splitOn.replaceAll(' ', '\\s')}$`)
          if (endingWithSplitOnRegExp.test(action.tagValue)) {
            const tagTextNode = editedTagContentEditableRef.current?.firstChild as Text
            /**
             * we must manually clear the text content, since the contentEditable span is not controlled by react
             */
            tagTextNode.textContent = ''
            return { ...prevState, tagValue: '' }
          }
          const splitOnRegExp = new RegExp(`${splitOn.replaceAll(' ', '\\s')}`)
          if (splitOnRegExp.test(action.tagValue)) {
            const separatorIdx = splitOnRegExp.exec(action.tagValue)?.index || 0
            const tagBefore = action.tagValue.slice(0, separatorIdx)
            const tagAfter = action.tagValue.slice(separatorIdx + splitOn.length).trim()
            const newTags = [...tags]
            const newState = { ...prevState, idx: prevState.idx + 1, isNew: true, tagValue: tagAfter }
            if (prevState.isNew) {
              newTags.splice(prevState.idx + 1, 0, tagBefore)
            } else {
              newTags[prevState.idx] = tagBefore
              newState.idx = prevState.idx
            }
            onChange?.(newTags)
            focusEditedTag()
            return newState
          }
          return { ...prevState, tagValue: action.tagValue }
        }
        case 'end': {
          if (prevState.isEditing && prevState.tagValue !== '') {
            const newTags = [...tags]
            if (prevState.isNew) {
              newTags.splice(prevState.idx + 1, 0, prevState.tagValue)
            } else {
              newTags[prevState.idx] = prevState.tagValue
            }
            onChange?.(newTags)
          }
          return { isEditing: false }
        }
        case 'abort':
          return { isEditing: false }
      }
      return prevState
    },
    [onChange, splitOn, tags],
  )
  const [pillEditState, dispatchPillEditAction] = useReducer(pillEditReducer, { isEditing: false })

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    if (newValue === splitOn) {
      setInputState('')
      return
    }
    if (newValue.includes(splitOn)) {
      const newSplitTags = newValue
        .slice(0, newValue.length - (newValue.endsWith(splitOn) ? splitOn.length : 0))
        .split(splitOn)
        .map((t) => t.trim())
        .filter((t) => t.length > 0)

      if (newSplitTags.length === 0) return

      const newTags = [...tags, ...newSplitTags]
      onChange?.(newTags)
      setInputState('')
      return
    }
    setInputState(newValue)
  }

  /**
   * Keep track of which pills are at the end of a line, so we can insert a clickable spacer that will be used to insert
   * a new pill in between the two pills.
   */
  useLayoutEffect(() => {
    /**
     * Bail early if:
     * - there's no wrapperMeasure (this happens on the first render)
     * - there's no wrapperRef (this never happens, it's here to appease typescript)
     * - we're editing a pill (we don't want to cause reflows, and besides, we're handling that case separately)
     */
    if (!wrapperMeasure || !wrapperRef.current || pillEditState.isEditing) return

    /**
     * We need to hide the spacers while we're measuring the pills. If we didn't do this, then our measurements would
     * be wrong because the spacers would be in the way.
     * Luckily we can use direct dom manipulation, which is synchronous, so we can just hide the spacers, measure, and
     * then show them again, with react none the wiser.
     */
    wrapperRef.current.querySelectorAll<HTMLDivElement>(`.${styles.eolInput}`).forEach((eolInput) => {
      eolInput.style.setProperty('display', 'none')
    })
    /**
     * First collect all the rects of each pill, so we can ask for rects only once (which causes a reflow)
     */
    const tagRects = tagRefs.flatMap((tagRef, idx) => {
      if (!tagRef.current) return []
      return { idx, rects: tagRef.current.getClientRects() }
    })

    /**
     * we want to know the position of the right edge of the inside of the wrapper
     * (not the right edge of the wrapper itself)
     */
    const wrapperRect = wrapperRef.current.getBoundingClientRect()
    const wrapperInnerRight = wrapperRect.right - thickness_wrapper_right_wall.current
    /**
     * Loop over each pill and check if the next pill is on the same line. If it's not, then we know that the current
     * pill is at the end of a line, and we save its index and the distance to the right edge of the wrapper (which
     * we'll use to position and size the spacer). We also have special handling for pills that wrap (it may or may not
     * be followed by another pill on the same line).
     */
    const pillsAtEnd = tagRects.flatMap(({ idx, rects }, rectIdx) => {
      const isLastRect = rectIdx === tagRects.length - 1
      const distanceToEdge = Math.floor(wrapperInnerRight - (last(rects)?.right || 0) - GAP_BETWEEN_TAGS)
      if (isLastRect) {
        if (distanceToEdge >= 28) {
          return [[idx, distanceToEdge] as const]
        }
        return []
      }

      const next = tagRects[rectIdx + 1]
      const isThereAnotherRectOnTheSameLine = Array.from(next.rects ?? []).some(
        (nextRect) => nextRect.top === last(rects)?.top,
      )

      if (!isThereAnotherRectOnTheSameLine) {
        if (distanceToEdge >= 0) {
          return [[idx, distanceToEdge] as const]
        }
      }
      return []
    })

    setPillsAtEndOfLine(new Map(pillsAtEnd))
    /**
     * Now that we're done measuring, we can show the spacers again. If we don't clean up, then the react shadow dom
     * will be out of sync with the real dom.
     * This will cause a reflow, but it's fine because we're done measuring.
     */
    wrapperRef.current.querySelectorAll<HTMLDivElement>(`.${styles.eolInput}`).forEach((eolInput) => {
      eolInput.style.removeProperty('display')
    })
  }, [pillEditState.isEditing, tagRefs, wrapperMeasure, wrapperRef])

  /**
   * If we're editing the last tag (via pillEditState, not via the input), then we need to handle the calculation of
   * the distance to the right edge of the wrapper, so we can position the input correctly.
   */
  useLayoutEffect(() => {
    /**
     * Bail early if:
     * - we're not editing a pill (this case is handled by the useLayoutEffect above)
     * - we don't have a ref to the edited pill (this never happens, it's here to appease typescript)
     */
    if (!pillEditState.isEditing || !editedTagContentEditableRef.current || !wrapperRef.current) return
    const isEditedTagLast = pillEditState.idx === tags.length - 1
    const lastPillRects = isEditedTagLast
      ? editedTagContentEditableRef.current.parentElement?.getClientRects()
      : last(tagRefs)?.current?.getClientRects()
    if (!lastPillRects) return

    const wrapperRect = wrapperRef.current.getBoundingClientRect()
    const wrapperInnerRight = wrapperRect.right - thickness_wrapper_right_wall.current
    const lastRectRight = last(lastPillRects)?.right || 0
    const distanceToEdge = Math.floor(wrapperInnerRight - lastRectRight - GAP_BETWEEN_TAGS)
    const newMap = new Map<number, number>()
    if (distanceToEdge >= 28) {
      newMap.set(tags.length - 1, distanceToEdge)
    }
    setPillsAtEndOfLine(newMap)
  }, [pillEditState, tagRefs, tags.length, wrapperRef])

  const handleRemoveTag = (idx: number) => () => {
    if (pillEditState.isEditing && pillEditState.idx === idx) {
      dispatchPillEditAction({ type: 'abort' })
      return
    }
    const newTags = [...tags]
    newTags.splice(idx, 1)
    onChange?.(newTags)
  }

  const handleInsertInBetweenPill = (idx: number) => () => {
    dispatchPillEditAction({ type: 'insert', afterIdx: idx })
  }

  /**
   * Try to find the character that was clicked on, as well as whether the click was closer to the left or right side of
   * the character.
   * Then, start editing the pill at that index. The reducer will take care of focusing the input and setting the caret
   */
  const handlePillClick = (idx: number) => (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    const pill = tagRefs[idx].current
    const pillTextSpanEl = pill?.querySelector<HTMLSpanElement>(`.${styles.tagText}`)
    const pillTextNode = pillTextSpanEl?.firstChild as Text
    const pillText = pillTextNode?.textContent
    if (!pillTextSpanEl || !pillTextNode || !pillText) return

    let caretPosition = 0
    for (let i = 0; i < pillText.length; i++) {
      const range = document.createRange()
      range.setStart(pillTextNode, i)
      range.setEnd(pillTextNode, i + 1)
      const rangeRect = range.getBoundingClientRect()
      const isClickWithinRect =
        e.clientX >= Math.floor(rangeRect.left) &&
        e.clientX <= Math.ceil(rangeRect.right) &&
        e.clientY >= Math.floor(rangeRect.top) &&
        e.clientY <= Math.ceil(rangeRect.bottom)
      if (isClickWithinRect) {
        // we found the character that was clicked on
        // now we need to find whether the click was closer to the left or right side of the character
        const distanceToRight = rangeRect.right - e.clientX
        const distanceToLeft = e.clientX - rangeRect.left
        caretPosition = distanceToRight < distanceToLeft ? i + 1 : i
        break
      }
    }
    dispatchPillEditAction({ type: 'startEdit', idx, caretPosition })
  }

  useLayoutEffect(() => {
    const inputSpacerRect = inputSpacerRef.current?.getBoundingClientRect()
    const wrapperRect = wrapperRef.current?.getBoundingClientRect()
    setInputWidth(clamp(inputSpacerRect?.width ?? 0, 28, wrapperRect?.width ?? 0))
  }, [inputState, wrapperRef])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (pillEditState.isEditing) {
      // we're editing a pill via the contentEditable span
      if (['Enter', 'Tab', 'Escape'].includes(e.key)) {
        e.preventDefault()
        e.stopPropagation()
      }
      switch (e.key) {
        case 'Enter':
        case 'Tab':
          dispatchPillEditAction({ type: 'end' })
          break
        case 'Backspace': {
          if (pillEditState.tagValue === '') {
            e.preventDefault()
            e.stopPropagation()
            dispatchPillEditAction({ type: 'abort' })
            if (!pillEditState.isNew) {
              onChange?.(tags.filter((_, idx) => idx !== pillEditState.idx))
            }
          }
          break
        }
        case 'Escape':
          dispatchPillEditAction({ type: 'abort' })
          break
      }
    } else if (inputRef.current !== null && document.activeElement === inputRef.current) {
      // we're editing (actually we're creating a pill) the last pill via the input
      if (['Enter', 'Tab', 'Escape'].includes(e.key)) {
        e.preventDefault()
        e.stopPropagation()
      }
      switch (e.key) {
        case 'Enter':
        case 'Tab': {
          if (inputState === '') return
          onChange?.([...tags, inputState.trim()])
          setInputState('')
          break
        }
        case 'Escape':
          setInputState('')
          break
        case 'Backspace': {
          const isEmptySelection = inputRef.current.selectionStart === inputRef.current.selectionEnd
          if (inputState === '' || (inputRef.current.selectionStart === 0 && isEmptySelection)) {
            e.preventDefault()
            e.stopPropagation()
            onChange?.(dropRight(tags))
          }
        }
      }
    }
  }

  const handlePasteOnContentEditable = (e: React.ClipboardEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const text = e.clipboardData?.getData('text/plain')
    if (!text || !editedTagContentEditableRef.current) return
    // we must manually clear the text content, since the contentEditable span is not controlled by react
    const selection = window.getSelection()
    if (!selection || selection.rangeCount <= 0 || !pillEditState.isEditing) return
    const { startOffset, endOffset } = selection.getRangeAt(0)
    const tagTextBefore = pillEditState.tagValue.slice(0, startOffset)
    const tagTextAfter = pillEditState.tagValue.slice(endOffset)
    const newTagText = `${tagTextBefore}${text}${tagTextAfter}`
    // splice in the clipboard text (without formatting)
    editedTagContentEditableRef.current.innerHTML = newTagText
    // keep react in sync
    dispatchPillEditAction({ type: 'update', tagValue: newTagText })
    // restore the selection
    const range = document.createRange()
    const selectionTargetNode = editedTagContentEditableRef.current.firstChild as Text
    range.setStart(selectionTargetNode, startOffset + text.length)
    range.setEnd(selectionTargetNode, startOffset + text.length)
    selection.removeAllRanges()
    selection.addRange(range)
  }

  const shouldShowPlaceholder = useMemo(() => {
    const noTags = tags.length === 0
    const emptyInput = inputState === ''
    return noTags && emptyInput && (useAddPill ? !showAddPill : true)
  }, [tags.length, inputState, useAddPill, showAddPill])

  const placeholderText = useMemo(() => {
    if (showAddPill || shouldShowPlaceholder) return undefined
    if (isSystemRedesignEnabled) return addMorePlaceholder || typeof placeholder === 'string' ? placeholder : undefined

    return addMorePlaceholder
  }, [showAddPill, shouldShowPlaceholder, addMorePlaceholder, placeholder, isSystemRedesignEnabled])

  const width = useMemo(() => {
    /**
     * The width of the input wrapper is either:
     * - 0 if the '+ Add' pill is visible (useAddPill && showAddPill)
     * - the full width of the wrapper, if the '+ Add' pill was clicked and distance to the right edge of the wrapper is less than the length of the '+ Add' pill
     * - the distance to the right edge of the wrapper, if it fits
     * - the full width of the wrapper, if it doesn't
     */
    const distanceToEdge = pillsAtEndOfLine.get(tags.length - 1)
    const fullWidth = wrapperMeasure?.width ?? 28

    if (useAddPill) {
      if (showAddPill) return 0
      if (distanceToEdge && distanceToEdge < ADD_PILL_WIDTH) return fullWidth
    }
    if (distanceToEdge === undefined) return fullWidth
    if (inputWidth <= distanceToEdge && distanceToEdge > MIN_INPUT_WIDTH) {
      return distanceToEdge
    } else {
      return fullWidth
    }
  }, [useAddPill, showAddPill, inputWidth, wrapperMeasure, tags.length, pillsAtEndOfLine])

  return (
    <div
      className={cx(
        styles.TagInput,
        wrapperClassName,
        styles[size],
        {
          [styles.maxWidth]: !!maxWidth,
          [styles.error]: !!error,
          [styles[variant]]: variant !== 'default',
          [styles.transparent]: transparent,
        },
        className,
      )}
      ref={wrapperRef}
      onKeyDown={handleKeyDown}
      style={{
        ...(minHeight ? { minHeight: minHeight } : {}),
      }}
    >
      {shouldShowPlaceholder && (
        <span className={cx(styles.placeholder, placeholderClassName, { [styles.withAddPill]: useAddPill })}>
          {placeholder}
        </span>
      )}
      {tags.map((tag, idx) => {
        const isLastOfLine = pillsAtEndOfLine.has(idx)
        const isLastTag = idx === tags.length - 1

        return (
          <React.Fragment key={idx}>
            {(!pillEditState.isEditing || pillEditState.idx !== idx || pillEditState.isNew) && (
              <div
                className={cx(styles.tag, { [styles.borderlessTag]: !showRemoveButton })}
                ref={tagRefs[idx]}
                style={
                  {
                    '--red': red,
                    '--green': green,
                    '--blue': blue,
                  } as CSSProperties
                }
                onClick={!showRemoveButton ? handleRemoveTag(idx) : undefined}
              >
                {variant === 'modern' ? (
                  <span className={styles.tagText} onClick={showRemoveButton ? handlePillClick(idx) : undefined}>
                    {tag}
                  </span>
                ) : (
                  <span onClick={showRemoveButton ? handlePillClick(idx) : undefined}>{tag}</span>
                )}
                {showRemoveButton && (
                  <button className={styles.removeTagBtn} onClick={handleRemoveTag(idx)}>
                    <Close size={12} />
                  </button>
                )}
              </div>
            )}
            {pillEditState.isEditing && pillEditState.idx === idx && (
              <div className={cx(styles.editedTag, { [styles.borderlessTag]: !showRemoveButton })}>
                <span
                  className={styles.textInput}
                  contentEditable={true}
                  ref={editedTagContentEditableRef}
                  onBlur={(e) => {
                    if (editedTagContentEditableRef.current?.parentElement?.contains(e.relatedTarget as Node)) {
                      return
                    }
                    dispatchPillEditAction({ type: 'end' })
                  }}
                  onPaste={handlePasteOnContentEditable}
                  onInput={() => {
                    dispatchPillEditAction({
                      type: 'update',
                      tagValue: editedTagContentEditableRef.current?.innerText ?? '',
                    })
                  }}
                  suppressContentEditableWarning
                >
                  {pillEditState.isNew ? '' : tag}
                </span>
                {showRemoveButton && (
                  <button className={styles.removeTagBtn} onClickCapture={handleRemoveTag(idx)}>
                    <Close size={12} />
                  </button>
                )}
              </div>
            )}
            {!pillEditState.isEditing && isLastOfLine && !isLastTag && (
              <div
                className={styles.eolInput}
                style={{ width: pillsAtEndOfLine.get(idx) }}
                onClick={handleInsertInBetweenPill(idx)}
              />
            )}
          </React.Fragment>
        )
      })}
      {useAddPill && showAddPill && (
        <div
          className={cx(styles.tag, styles.addTagWrapper)}
          onClick={() => {
            inputRef.current?.focus()
            setShowAddPill(false)
          }}
          style={
            {
              '--red': red,
              '--green': green,
              '--blue': blue,
            } as CSSProperties
          }
        >
          <Plus size={isSystemRedesignEnabled || size === 'sm' ? 16 : 20} />
          <span className={cx(styles.tagText, styles.addTag)}>Add</span>
        </div>
      )}
      <div
        className={cx(styles.inputWrapper, {
          [styles.inputWithBorder]: inputWithBorder,
          [styles.noTags]: tags.length === 0,
        })}
        style={{ width }}
      >
        <input
          {...rest}
          type="text"
          style={{ width: width === 0 ? 0 : undefined, padding: width === 0 ? 0 : undefined }}
          className={styles.input}
          value={inputState}
          onChange={handleInputChange}
          ref={inputRef}
          onBlur={() => {
            if (useAddPill) setShowAddPill(true)
            if (inputState === '') return
            onChange?.([...tags, inputState.trim()])
            setInputState('')
          }}
          onFocus={() => {
            if (useAddPill) setShowAddPill(false)
          }}
          placeholder={placeholderText as string}
        />
        {inputWithBorder && !showAddPill && (
          <div className={styles.arrowIcon}>
            <ArrowRight size={16} />
          </div>
        )}
        <span className={styles.inputSpacer} ref={inputSpacerRef}>
          {inputState}
        </span>
      </div>
    </div>
  )
}
